import type { ActorEvent, GalleryViewConfig, Pagination, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import { ARRANGEMENT, BLOCK_SIZE, DIRECTION, GALLERY_CARD_STYLE } from '@lighthouse/core'
import { conversionHighLightRecord, getViewColumns, ViewEmptyState } from '@lighthouse/shared'
import { BreakPointSize } from '@lighthouse/tools'
import type { FC, ReactNode } from 'react'
import React, { memo, useMemo } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { HorizontalScrollView } from './HorizontalScrollView'
import { VerticalView } from './VerticalView'

interface GalleryBlockProps {
    scale?: number
    breakPoint: BreakPointSize
    blockWidth?: number
    pagination: Pagination
    renderItem?: (record: RecordLikeProtocol) => ReactNode
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onLoadMoreData?: (pageNum: number) => Promise<RecordLikeProtocol[]>
}

/** 画廊Block */
const GalleryBlock: FC<GalleryBlockProps> = props => {
    const {
        scale,
        breakPoint,
        blockWidth,
        pagination,
        renderItem,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderRichText,
        onLoadMoreData
    } = props
    const { blockData, dataSource, recordPoints } = useViewBlockContext()
    const {
        schema,
        viewOptions: { tableProps }
    } = dataSource
    const { id, title, config } = blockData
    const {
        pointer,
        headCover,
        headAvatar,
        headTitle,
        headTags,
        contentTags,
        footTags,
        style = GALLERY_CARD_STYLE.block,
        highLightRules = [],
        viewFieldSettings,
        eventList,
        recordOperator,
        breakPoint: baseBreakPoint,
        canPaginate,
        anchor
    } = config as GalleryViewConfig
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { viewLayout, emptyView } = baseBreakPoint || {}
    const { arrangement, cols, size = BLOCK_SIZE.md, direction = DIRECTION.vertical } = viewLayout || {}
    const noData = !pointer
    const isHorizontal = direction === DIRECTION.horizontal

    // block 设置后的tableProps
    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId: id,
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [id, schema, tableProps, viewFieldSettings]
    )

    const autoCols = useMemo(() => {
        const xLargeSize = new Map([
            [BreakPointSize.lg, 2],
            [BreakPointSize.md, 1],
            [BreakPointSize.sm, 1],
            [BreakPointSize.xs, 1]
        ])
        const largeSize = new Map([
            [BreakPointSize.lg, 3],
            [BreakPointSize.md, 2],
            [BreakPointSize.sm, 1],
            [BreakPointSize.xs, 1]
        ])
        const middleSize = new Map([
            [BreakPointSize.lg, 4],
            [BreakPointSize.md, 3],
            [BreakPointSize.sm, 2],
            [BreakPointSize.xs, 1]
        ])
        const smallSize = new Map([
            [BreakPointSize.lg, 5],
            [BreakPointSize.md, 4],
            [BreakPointSize.sm, 3],
            [BreakPointSize.xs, 2]
        ])

        const sizeMap = new Map([
            [BLOCK_SIZE.xl, xLargeSize],
            [BLOCK_SIZE.lg, largeSize],
            [BLOCK_SIZE.md, middleSize],
            [BLOCK_SIZE.sm, smallSize]
        ])

        return sizeMap.get(size)?.get(breakPoint) ?? 3
    }, [breakPoint, size])

    const { row: rowStyleConditions } = useMemo(() => conversionHighLightRecord(highLightRules), [highLightRules])

    const isMobile = breakPoint === BreakPointSize.xs
    const finalCols =
        arrangement === ARRANGEMENT.fixed && cols ? (isMobile ? Math.min(2, cols) : cols) : isHorizontal ? autoCols + 0.3 : autoCols

    return useMemo(() => {
        if (noData || recordPoints.length === 0) {
            return (
                <ViewEmptyState
                    id={effectiveAnchorId}
                    config={emptyView}
                    onRenderRichText={onRenderRichText}
                    rootStyle={{ padding: '16px 0' }}
                />
            )
        }

        if (isHorizontal) {
            return (
                <HorizontalScrollView
                    key="horizontal"
                    id={effectiveAnchorId}
                    scale={scale}
                    cols={finalCols}
                    canPaginate={canPaginate}
                    pagination={pagination}
                    headCover={headCover}
                    columns={settingColumns}
                    rowStyleConditions={rowStyleConditions}
                    headAvatar={headAvatar}
                    headTitle={headTitle}
                    headTags={headTags}
                    contentTags={contentTags}
                    footTags={footTags}
                    recordStyle={style}
                    eventList={eventList}
                    recordOperator={recordOperator}
                    renderItem={renderItem}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderRichText}
                    onLoadMoreData={onLoadMoreData}
                />
            )
        }

        return (
            <VerticalView
                key="vertical"
                id={effectiveAnchorId}
                cols={finalCols}
                headCover={headCover}
                columns={settingColumns}
                rowStyleConditions={rowStyleConditions}
                headAvatar={headAvatar}
                headTitle={headTitle}
                headTags={headTags}
                contentTags={contentTags}
                footTags={footTags}
                recordStyle={style}
                renderItem={renderItem}
                onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                onRecordClick={onRecordClick}
                onRenderButtonTitle={onRenderRichText}
            />
        )
    }, [
        noData,
        recordPoints.length,
        isHorizontal,
        finalCols,
        headCover,
        settingColumns,
        rowStyleConditions,
        headAvatar,
        headTitle,
        headTags,
        contentTags,
        footTags,
        style,
        renderItem,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderRichText,
        effectiveAnchorId,
        emptyView,
        scale,
        canPaginate,
        pagination,
        eventList,
        recordOperator,
        onLoadMoreData
    ])
}

export default memo(GalleryBlock)
